import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Services.css";
import useScrollReveal from "../hooks/useScrollReveal.js";
import useSeo, { buildBreadcrumbs, combineStructuredData, SITE_URL } from "../hooks/useSeo.js";

const services = [
  {
    id: 1,
    key: "carpet",
    name: "Carpet and Rug Cleaning",
    image: "carpet&rug.jpg",
    tagline: "Deep cleaning for fresher carpets and rugs.",
    description:
      "We use hot-water extraction to lift embedded dirt, stains, allergens and odours, helping restore the look, feel and freshness of your carpets and rugs.",
    price: "From $45 per room",
    details: [
      { description: "Carpet cleaning (1-2 rooms)", price: "$45 / room" },
      { description: "Carpet cleaning (3 rooms)", price: "$120 package" },
      { description: "Carpet cleaning (4+ rooms)", price: "$120 + $40 / extra room" },
      { description: "Rug cleaning", price: "$45 / rug" },
    ],
    extras: [
      {
        icon: "🧶",
        title: "Carpet Cleaning",
        description: [
          "Deep cleaning that lifts embedded dirt, refreshes flattened fibres and gives tired carpets a cleaner, brighter appearance.",
        ],
        tagline: "Less dirt. More colour. A carpet worth walking barefoot on again.",
      },
      {
        icon: "🧵",
        title: "Rug Cleaning",
        description: [
          "Your rug may be part of your décor—or part of your story.",
          "We carefully assess and clean each rug to remove built-up dust, spills, stains and odours while protecting its fibres, colours and character.",
        ],
        tagline: "Cleaned with care. Preserved with purpose.",
      },
    ],
  },
  {
    id: 2,
    key: "upholstery",
    name: "Upholstery and Couch Cleaning",
    image: "upholstery&mattress.jpg",
    tagline: "Comfort that feels as clean as it looks.",
    description:
      "We remove embedded dirt, body oils, food spills, stains, pet dander and odours to leave your couch and upholstery cleaner, brighter and refreshed.",
    price: "From $40 per seat",
    details: [{ description: "Couch / seat cleaning", price: "$40 / seat" }],
    extras: [
      {
        icon: "🛋️",
        title: "Couch & Upholstery Cleaning",
        description: [
          "Your couch holds more than cushions. It holds family moments, quiet nights, children's memories and years of everyday life.",
          "We remove hidden dirt, body oils, food particles, pet hair and odours to help your furniture look refreshed and feel inviting again.",
        ],
        tagline: "Before replacing it, let Aeti revive it.",
      },
    ],
  },
  {
    id: 3,
    key: "mattress",
    name: "Mattress Cleaning",
    image: "mattress-cleaning.jpg",
    tagline: "A deeper clean for a fresher mattress.",
    description:
      "We deep-clean mattresses to help remove body oils, sweat, stains, allergens, dust mites and odours, leaving them cleaner and fresher.",
    price: "From $120 per mattress",
    details: [
      { description: "Mattress cleaning (Single)", price: "$120 each" },
      { description: "Mattress cleaning (Double/Queen/King)", price: "$180 each" },
    ],
    extras: [
      {
        icon: "🛏️",
        title: "Mattress Cleaning",
        description: [
          "Your mattress can collect dust, sweat, body oils and everyday allergens beneath the surface.",
          "Our deep-cleaning treatment refreshes the fabric and helps create a cleaner, fresher sleeping environment.",
        ],
        tagline: "A cleaner mattress for a fresher night's sleep.",
      },
    ],
  },
  {
    id: 4,
    key: "curtains",
    name: "Curtains and Blinds Cleaning",
    image: "curtains&blinds.webp",
    tagline: "Let the light in without the dust",
    description:
      "Refresh window curtains and door curtains or blinds to remove dust, allergens and everyday buildup.",
    price: "From $80 per set",
    details: [
      { description: "Curtain cleaning (Window set)", price: "$80 / set" },
      { description: "Curtain cleaning (Door set)", price: "$100 / set" },
    ],
    extras: [
      {
        icon: "🪟",
        title: "Curtain Cleaning",
        description: [
          "Your curtains hold more than fabric. They frame family mornings, quiet evenings, natural light and the everyday moments that make a house feel like home.",
          "We remove trapped dust, odours, pet hair and everyday buildup to help your curtains look fresher, feel cleaner and bring softness back to your space.",
        ],
        tagline: "Lets Aeti IT",
      },
    ],
  },
  {
    id: 5,
    key: "window",
    name: "Window (Glass) Cleaning",
    image: "windows-glass-cleaning.jpg",
    tagline: "Clear glass, inside and out",
    description:
      "Streak-free cleaning for all glass surfaces; windows and doors, inside and out, for a brighter, clearer finish.",
    price: "From $25 per pane",
    details: [
      { description: "Glass cleaning (Window pane)", price: "$25 / pane" },
      { description: "Glass cleaning (Door pane)", price: "$40 / pane" },
    ],
  },
];

const servicesStructuredData = combineStructuredData(
  {
    "@type": "ItemList",
    itemListElement: services.map((service, index) => ({
      "@type": "Service",
      position: index + 1,
      name: service.name,
      description: service.description,
      url: `${SITE_URL}/services#${service.key}`,
      areaServed: "Perth, Western Australia",
      provider: { "@type": "LocalBusiness", name: "Aeti Cleaning Services" },
    })),
  },
  buildBreadcrumbs([
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
  ])
);

const Services = () => {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(null);
  const [expandedExtras, setExpandedExtras] = useState(null);
  const pageRef = useRef(null);

  useScrollReveal(pageRef);
  useSeo({
    title: "Cleaning Services Perth | Carpet, Rug, Couch & Upholstery Cleaning | Aeti",
    rawTitle: true,
    description:
      "Explore Aeti carpet, rug, couch, upholstery, mattress, curtain, blind and window cleaning services across Perth and surrounding suburbs.",
    path: "/services",
    structuredData: servicesStructuredData,
  });

  const toggleDetails = (id) => {
    setShowDetails(showDetails === id ? null : id);
  };

  const toggleExtras = (id) => {
    setExpandedExtras(expandedExtras === id ? null : id);
  };

  const handleQuoteRequest = (service) => {
    navigate('/get-quote', { state: { preselectedCategory: service.key } });
  };

  return (
    <div className="services-page" ref={pageRef}>
      <section className="services-hero reveal">
        <div className="services-hero-image">
          <img
            src={require("../assets/deep.jpg")}
            alt="Aeti cleaning team arriving with equipment for a deep clean"
          />
        </div>
        <div className="services-hero-copy">
          <p className="eyebrow">Aeti offers cleaning services throughout Perth and its surrounding suburbs across Western Australia</p>
          <h1>Cleaning Services Built Around Your Home</h1>
          <p>
            Aeti provides professional Carpet Cleaning and Specialist Cleaning Services for Rug, Couch, Upholstery, Mattress, Blinds, Curtains and Windows across Perth and Western Australia.
          </p>
        </div>
      </section>

      <div className="services-grid">
        {services.map((service, index) => (
          <article
            className="service-card reveal"
            key={service.id}
            id={service.key}
            style={{ transitionDelay: `${index * 90}ms` }}
          >
            {service.image && (
              <div className="service-card-image">
                <img src={require(`../assets/${service.image}`)} alt={service.name} />
              </div>
            )}
            <div className="service-card-top">
              <h2>{service.name}</h2>
              <p className="service-tagline">{service.tagline}</p>
              <p className="description">{service.description}</p>
              <p className="price">{service.price}</p>
            </div>

            {service.extras && (
              <div className="service-extras">
                <div className="service-extras-preview">
                  {service.extras.map((extra) => (
                    <span className="service-extra-chip" key={extra.title}>
                      {extra.icon} {extra.title}
                    </span>
                  ))}
                </div>

                {expandedExtras === service.id && (
                  <div className="service-extras-full">
                    {service.extras.map((extra) => (
                      <div className="service-extra-item" key={extra.title}>
                        <h4>
                          <span aria-hidden="true">{extra.icon}</span> {extra.title}
                        </h4>
                        {extra.description.map((paragraph, index) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                        <p className="service-extra-tagline">{extra.tagline}</p>
                      </div>
                    ))}
                  </div>
                )}

                <button type="button" className="read-more-btn" onClick={() => toggleExtras(service.id)}>
                  {expandedExtras === service.id ? "Show less ▾" : "Read more ▸"}
                </button>
              </div>
            )}

            <div className="service-actions">
              <button className="details-btn" onClick={() => toggleDetails(service.id)}>
                {showDetails === service.id ? "Hide pricing" : "View pricing"}
              </button>
              <button className="quote-btn" onClick={() => handleQuoteRequest(service)}>
                Request a quote
              </button>
            </div>

            {showDetails === service.id && (
              <div className="pricing-details">
                <ul>
                  {service.details.map((item, index) => (
                    <li key={index}>
                      <span>{item.description}</span>
                      <strong>{item.price}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </article>
        ))}

        <article className="service-card launching-soon-card reveal" style={{ transitionDelay: `${services.length * 90}ms` }}>
          <div className="service-card-top">
            <div className="service-card-badge launching-soon-badge">Launching soon</div>
            <h2>Laundry &amp; Delivery</h2>
            <p className="service-tagline">The practical service that saves time</p>
            <p className="description">
              Washing, drying, ironing and folding with effortless delivery — coming soon to Aeti Cleaning Services.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Services;
